/**
 * Tiny markdown subset renderer for content_pages.body_md.
 *
 * Why a hand-rolled subset and not `marked` (~30kB)?
 *  - The content we render is admin-curated. We need only headings, paragraphs,
 *    inline links, bold, inline code, and simple lists.
 *  - Every byte saved on the static-page bundle is a Lighthouse win.
 *  - The sanitization model is easier to reason about: only escaped text +
 *    a curated set of inline tokens are emitted.
 *
 * Output is a string of safe HTML. Callers may pass it to React via
 * `dangerouslySetInnerHTML={{ __html }}` because every code path here either
 * HTML-escapes its input or only emits a fixed tag with an attribute that has
 * been validated.
 */

export function renderMarkdown(input: string): string {
  if (!input) return '';

  const text = input.replace(/\r\n/g, '\n').replace(/[ \t]+$/gm, '');
  const blocks = text.split(/\n{2,}/);
  const out: string[] = [];

  for (const raw of blocks) {
    const block = raw.trimEnd();
    if (!block) continue;

    const heading = /^(#{1,6})\s+(.+)$/.exec(block);
    if (heading) {
      const level = heading[1].length;
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    if (/^- /.test(block)) {
      const items = block
        .split('\n')
        .filter((l) => /^- /.test(l))
        .map((l) => `<li>${renderInline(l.replace(/^- /, ''))}</li>`)
        .join('');
      out.push(`<ul>${items}</ul>`);
      continue;
    }

    if (/^\d+\.\s/.test(block)) {
      const items = block
        .split('\n')
        .filter((l) => /^\d+\.\s/.test(l))
        .map((l) => `<li>${renderInline(l.replace(/^\d+\.\s/, ''))}</li>`)
        .join('');
      out.push(`<ol>${items}</ol>`);
      continue;
    }

    const tableLines = block.split('\n');
    if (
      tableLines.length >= 2 &&
      tableLines[0].includes('|') &&
      isTableSeparatorRow(tableLines[1])
    ) {
      out.push(renderTable(tableLines));
      continue;
    }

    const lines = block.split('\n').map((l) => renderInline(l));
    out.push(`<p>${lines.join('<br>')}</p>`);
  }

  return out.join('\n');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeHref(url: string): string | null {
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^tel:/i.test(trimmed)) return trimmed;
  if (/^\/[^\s]*$/.test(trimmed)) return trimmed;
  return null;
}

/** A GFM table separator row, e.g. `| --- | --- |` or `---|---`. No pipe escaping needed — admin-authored content, not user input. */
function isTableSeparatorRow(line: string): boolean {
  return /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(line);
}

function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((c) => c.trim());
}

/** `lines[0]` is the header row, `lines[1]` is the separator (already validated), the rest are body rows. */
function renderTable(lines: string[]): string {
  const head = splitTableRow(lines[0])
    .map((c) => `<th>${renderInline(c)}</th>`)
    .join('');
  const body = lines
    .slice(2)
    .filter((l) => l.trim().length > 0)
    .map(
      (l) =>
        `<tr>${splitTableRow(l)
          .map((c) => `<td>${renderInline(c)}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function renderInline(text: string): string {
  // Two-pass extract → escape → reinsert. Sentinel is `@@SKFTOK_<n>_@@`
  // which contains no characters meaningful to either markdown or HTML
  // escaping, so we can safely escape the surrounding text in between.
  const tokens: string[] = [];
  const place = (html: string): string => {
    tokens.push(html);
    return `@@SKFTOK_${tokens.length - 1}_@@`;
  };

  let working = text;

  working = working.replace(/`([^`]+)`/g, (_m, code: string) =>
    place(`<code>${escapeHtml(code)}</code>`),
  );

  working = working.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, url: string) => {
    const href = safeHref(url);
    if (!href) return place(escapeHtml(label));
    return place(`<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`);
  });

  working = working.replace(/\*\*([^*]+)\*\*/g, (_m, inner: string) =>
    place(`<strong>${escapeHtml(inner)}</strong>`),
  );

  working = working.replace(/\*([^*\s][^*]*?)\*/g, (_m, inner: string) =>
    place(`<em>${escapeHtml(inner)}</em>`),
  );

  let escaped = escapeHtml(working);

  escaped = escaped.replace(/@@SKFTOK_(\d+)_@@/g, (_m, i: string) => {
    return tokens[Number(i)] ?? '';
  });

  return escaped;
}
